/* eslint-disable @atlassian/relay/unused-fields */
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { createResource } from 'react-resource-router-next';
import { fetchQuery, graphql, type IEnvironment } from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags';

import type {
	AssignmentDeletionRequest,
	AssignmentEditRequest,
	AssignmentSubmissionRequest,
} from '../common/types/assignments';
import type { Group, GroupSearchRequest } from '../common/types/groups';
import type {
	AppSwitcherApiAvailableProductsResponse,
	AppSwitcherApiOrg,
	AvailableWorkspacesResponse,
	OrgContextApi,
} from '../common/types/org-context';
import type { AssignmentRoleResponse, PermissionsResponse } from '../common/types/permissions';
import type { SpaceResponse, SpacesListResponse } from '../common/types/spaces';

import type { services_RecentActivityForWorkspaceSelectionQuery } from './__generated__/services_RecentActivityForWorkspaceSelectionQuery.graphql';

declare global {
	interface Window {
		STUDIO_APP_VERSION: string;
	}
}

export const fetchOrgContextData = async (workspaceId: string): Promise<OrgContextApi> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/context`,
	);
	if (!response.ok) {
		throw new Error('Failed to fetch studio workspace context data');
	}
	return response.json();
};

export const orgContextQueryOptions = (workspaceId: string) =>
	queryOptions({
		queryKey: getOrgContextQueryKey(workspaceId),
		queryFn: () => fetchOrgContextData(workspaceId),
		enabled: !!workspaceId,
		staleTime: Infinity,
	});

export const fetchAvailableOrgs = async (): Promise<AppSwitcherApiAvailableProductsResponse> => {
	const response = await fetch('/gateway/api/app-switcher/api/available-products?source=studio');
	if (!response.ok) {
		throw new Error('Failed to fetch available organisations');
	}
	return response.json();
};

export const fetchAvailableWorkspaces = async (
	uniqueOrgs: AppSwitcherApiOrg[],
): Promise<AvailableWorkspacesResponse> => {
	const isAppSelectionChangeEnabled = fg('asf620_route_arch_and_app_selection_changes');
	const isGetOrgListInStudioBeEnabled = fg('asf-828-get-org-list-in-studio-be');
	const response = await fetch('/gateway/studio/api/studio/organization/workspace', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body:
			isAppSelectionChangeEnabled && isGetOrgListInStudioBeEnabled
				? uniqueOrgs.length > 0
					? JSON.stringify(uniqueOrgs.map((org) => org.id))
					: undefined
				: JSON.stringify(uniqueOrgs.map((org) => org.id)),
	});
	if (!response.ok) {
		throw new Error('Failed to fetch available workspaces');
	}
	return response.json();
};

export const getOrgContextQueryKey = (workspaceId?: string) => ['org-context', workspaceId];
export const getAvailableOrgsQueryKey = (): string[] => ['app-switcher-api-available-products'];
export const getAvailableWorkspacesQueryKey = (uniqueOrgs: AppSwitcherApiOrg[]): string[] => {
	const queryKey = ['studio-BE-available-workspaces', ...uniqueOrgs.map((org) => org.id)];
	return queryKey;
};
export const getWorkspaceIdBySiteIdQueryKey = (siteId: string): string[] => [
	'workspace-id-by-site-id',
	siteId,
];
export const getRecentWorkspaceForUserQueryKey = (atlassianAccountId: string): string[] => [
	'recent-workspace-for-user',
	atlassianAccountId,
];

export const fetchSpaceBySpaceId = async (
	workspaceId: string,
	spaceId: string,
): Promise<SpaceResponse> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/spaces/${spaceId}`,
	);
	if (!response.ok) {
		throw new Error('Failed to fetch space by space id');
	}
	return response.json();
};

export const getSpaceQueryKey = (workspaceId: string, spaceId: string): string[] => [
	'space',
	workspaceId,
	spaceId,
];
export const getSpaceQueryOptions = (workspaceId: string, spaceId: string) =>
	queryOptions({
		queryKey: getSpaceQueryKey(workspaceId, spaceId),
		queryFn: () => fetchSpaceBySpaceId(workspaceId, spaceId),
		enabled: Boolean(workspaceId) && Boolean(spaceId),
		staleTime: Infinity,
	});

export const fetchSpacesApi = async (
	workspaceId: string,
	opts: { query?: string; after?: string | null },
	errorMessage: string,
): Promise<SpacesListResponse> => {
	const params = new URLSearchParams();
	if (opts?.query) {
		params.set('query', opts.query);
	}
	if (opts?.after) {
		params.set('after', opts.after);
	}

	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/spaces?${params.toString()}`,
	);

	if (!response.ok) {
		throw new Error(errorMessage);
	}
	return response.json();
};

type SpacesPageParam = {
	cursor: string | null;
	search?: string;
};

export const getSpacesInfiniteQueryKey = (workspaceId: string, search?: string): string[] => [
	'spaces-infinite',
	workspaceId,
	search || '',
];

export const getSpacesInfiniteQueryOptions = (workspaceId: string, search?: string) =>
	infiniteQueryOptions({
		queryKey: getSpacesInfiniteQueryKey(workspaceId, search),
		queryFn: ({ pageParam }) =>
			fetchSpacesApi(
				workspaceId,
				{
					query: search,
					after: pageParam?.cursor ?? null,
				},
				'Failed to fetch spaces',
			),
		initialPageParam: null,
		getNextPageParam: (lastPage: SpacesListResponse) => {
			if (!lastPage.hasNext || !lastPage.nextCursor) {
				return null;
			}

			return {
				cursor: lastPage.nextCursor,
				search,
			} as SpacesPageParam;
		},
		staleTime: Infinity,
	});

export const createSpace = async (
	workspaceId: string,
	data: { name: string },
): Promise<SpaceResponse> => {
	const response = await fetch(`/gateway/studio/workspace/${workspaceId}/api/studio/spaces`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) {
		throw new Error(`Failed to create space: ${response.status} ${response.statusText}`);
	}
	return response.json();
};

export const getSpacesQueryKey = (workspaceId: string): string[] => ['spaces', workspaceId];

export const getWorkspaceIdBySiteId = async (
	siteId: string,
): Promise<{ id: string | undefined }> => {
	const response = await fetch(`/gateway/studio/api/studio/site/${siteId}/workspace`);
	if (!response.ok && response.status !== 404) {
		throw new Error('Failed to fetch workspace ID by site ID');
	}

	// 404 is returned when there are no v2 workspaces for the site
	if (response.status === 404) {
		return { id: undefined };
	}

	return response.json();
};

export const getRecentWorkspaceForUser = async (
	environment: IEnvironment,
): Promise<string | undefined> => {
	const data = await fetchQuery<services_RecentActivityForWorkspaceSelectionQuery>(
		environment,
		graphql`
			query services_RecentActivityForWorkspaceSelectionQuery {
				activity {
					myActivity {
						all(
							filter: {
								arguments: {
									products: ["studio"]
									objectTypes: ["workspace"]
									eventTypes: ["viewed"]
								}
							}
							first: 1
						) {
							edges {
								node {
									object {
										id
										type
										product
										rootContainerId
									}
									event {
										eventType
										timestamp
									}
								}
							}
						}
					}
				}
			}
		`,
		{},
	).toPromise();

	const id = data?.activity?.myActivity?.all?.edges?.[0]?.node?.object?.id;
	if (!id) {
		return undefined;
	}
	return id.includes('/') ? id.split('/').pop() : id;
};

export const permissionResponse = createResource<PermissionsResponse>({
	type: 'PERMISSION',
	getKey: (routerContext) => {
		const workspaceId = routerContext.match.params.workspaceId;
		const { searchText } = routerContext.query;
		return `permissions-${workspaceId}-${searchText || ''}`;
	},
	getData: async (routerContext): Promise<PermissionsResponse> => {
		const workspaceId = routerContext.match.params.workspaceId;
		if (!workspaceId) {
			return {
				assignments: [],
				roles: [],
			};
		}

		const response = await fetch(
			`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/permissions/roles`,
		);

		if (!response.ok) {
			throw new Error('Failed to fetch permissions');
		}
		const responseBody = await response.json();
		return {
			assignments: responseBody?.assignments,
			roles:
				responseBody?.roles?.filter(
					(role: AssignmentRoleResponse) => role.roleType !== 'AGENT_CREATOR',
				) ?? [],
		};
	},
	isBrowserOnly: true,
});

export const searchGroups = async (
	workspaceId: string,
	request: GroupSearchRequest,
): Promise<Group[]> => {
	const response = await fetch(`/gateway/studio/workspace/${workspaceId}/api/studio/group/search`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(request),
	});
	if (!response.ok) {
		throw new Error('Failed to search groups');
	}
	return response.json();
};

export const getGroupsQueryKey = (workspaceId: string, nameContains?: string): string[] => [
	'groups-search',
	workspaceId,
	nameContains || '',
];

export const getGroupsQueryOptions = (workspaceId: string, nameContains: string = '') =>
	queryOptions({
		queryKey: getGroupsQueryKey(workspaceId, nameContains),
		queryFn: () => searchGroups(workspaceId, { nameContains }),
		enabled: Boolean(workspaceId),
		staleTime: Infinity,
	});

/**
 * Submits principal role assignments to the workspace permissions API.
 * POST /api/studio/workspace/permissions/roles
 */
export const submitAssignment = async (
	workspaceId: string,
	data: AssignmentSubmissionRequest,
): Promise<void> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/permissions/roles`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		},
	);
	if (!response.ok) {
		throw new Error('Failed to submit assignment');
	}
};

export const deleteAssignment = async (
	workspaceId: string,
	data: AssignmentDeletionRequest,
): Promise<void> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/permissions/roles`,
		{
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		},
	);
	if (!response.ok) {
		throw new Error('Failed to delete assignment');
	}
};

export const editAssignment = async (
	workspaceId: string,
	data: AssignmentEditRequest,
): Promise<void> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/permissions/roles`,
		{
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		},
	);
	if (!response.ok) {
		throw new Error('Failed to edit assignment');
	}
};
